import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { TopMaintenanceTagDashboardDto } from 'api/dashboard/dtos/TopMaintenanceTagDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { VictoryLabel, VictoryPie } from 'victory';
import Utils from 'common/services/Utils';
import useElementSize from 'common/hooks/useElementSize';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import openMaintenancesList from '../utils/openMaintenancesList';
import styles from './TopMaintenanceTags.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';

const DONUT_COLORS = ['#02c7c7', '#038cb6', '#7c5cbf', '#e8873d', '#e05a8a', '#d4a832'];

type Props = {
	// Legacy props (direct data passing)
	data?: TopMaintenanceTagDashboardDto[] | null;
	isLoading?: boolean;
	// New props (self-fetch mode)
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const TopMaintenanceTags = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [hiddenData, setHiddenData] = useState<string[]>([]);
	const [containerRef, { width, height }] = useElementSize();
	const [fetchedData, setFetchedData] = useState<TopMaintenanceTagDashboardDto[] | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);
	const [tooltip, setTooltip] = useState<{ name: string; value: number; color: string; clientX: number; clientY: number } | null>(null);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('topMaintenanceTags');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());

	const isLegacyMode = props.data !== undefined;

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			setFetchLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getTopMaintenanceTags(criteria);
				if (!cancelled) {
					setFetchedData(result);
				}
			} catch {
				if (!cancelled) {
					addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				}
			} finally {
				if (!cancelled) {
					setFetchLoading(false);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode, dateFrom, dateTo]);

	const data = isLegacyMode ? props.data! : fetchedData;
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	const getTypeName = (item: TopMaintenanceTagDashboardDto) => {
		return item.isSystem ? t(`common.maintenance_contract_type.${item.typeName}` as any) as string : item.typeName;
	};

	if (!data || data.length === 0) {
		return (
			<Box
				className={tableStyles.container}
				title={t('dashboard.top_maintenance_tags') as string}
				subTitleTooltip={t('dashboard.top_maintenance_tags_tooltip' as any) as string}
				renderOptions={!isLegacyMode ? <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} /> : undefined}
			>
				{isLoading ? (
					<div className={tableStyles.centerAlign}>
						<LoadingSpinner />
					</div>
				) : (
					<WidgetNoData />
				)}
			</Box>
		);
	}

	const itemsData = data.filter(x => !hiddenData.includes(x.typeId));
	const colorScale = itemsData.map(item => DONUT_COLORS[data.indexOf(item) % DONUT_COLORS.length]);

	const grandTotal = data.reduce((sum, x) => sum + x.totalCost, 0);
	const total = itemsData.reduce((sum, x) => sum + x.totalCost, 0);

	const onClickLegend = (typeId: string) => {
		if (hiddenData.includes(typeId)) {
			setHiddenData(hiddenData.filter(x => x !== typeId));
		} else {
			setHiddenData([...hiddenData, typeId]);
		}
	};

	// Layout calculations — legend goes right when wide, bottom when narrow (same pattern as VehiclesBySegment)
	const legendItemCount = data.length;
	const estimatedLegendHeight = Math.max(40, Math.ceil(legendItemCount / 3) * 22);
	const legendSideWidth = 180;

	const bottomDonutSize = Math.min(width, Math.max(height - estimatedLegendHeight, 0));
	const sideDonutSize = Math.min(Math.max(width - legendSideWidth, 0), height);
	const isSideLayout = sideDonutSize > bottomDonutSize;

	const donutAreaWidth = isSideLayout ? width - legendSideWidth : width;
	const donutAreaHeight = isSideLayout ? height : Math.max(height - estimatedLegendHeight, 150);
	const donutSize = Math.min(donutAreaWidth, donutAreaHeight);
	const pieRadius = donutSize * 0.44;
	const pieInnerRadius = pieRadius * 0.7467;

	// Pie events for tooltip
	// eslint-disable-next-line
	const pieEvents: any[] = [
		{
			target: 'data',
			eventHandlers: {
				onMouseEnter: (evt: any, targetProps: any) => {
					const idx = targetProps.index as number;
					const item = itemsData[idx];
					if (!item) return [];
					const origIndex = data.indexOf(item);
					const color = DONUT_COLORS[(origIndex >= 0 ? origIndex : idx) % DONUT_COLORS.length];
					setTooltip({
						name: getTypeName(item),
						value: item.totalCost,
						color,
						clientX: evt.clientX,
						clientY: evt.clientY,
					});
					return [];
				},
				onMouseMove: (evt: any) => {
					setTooltip(prev => prev ? { ...prev, clientX: evt.clientX, clientY: evt.clientY } : null);
					return [];
				},
				onMouseLeave: () => {
					setTooltip(null);
					return [];
				},
				onClick: (_evt: any, targetProps: any) => {
					const idx = targetProps.index as number;
					const item = itemsData[idx];
					if (!item) return [];
					openMaintenancesList({
						dateFrom,
						dateTo,
						typeId: item.typeId,
						typeName: getTypeName(item),
					});
					return [];
				},
			},
		},
	];

	const sortedItems = Utils.sortAlphabetically(
		data.map((item, i) => ({ item, color: DONUT_COLORS[i % DONUT_COLORS.length], name: getTypeName(item) })),
		false,
		'name',
	);

	const chartContent = (
		<div className={`${styles.chartContainer} ${isSideLayout ? styles.chartContainerSide : ''}`} ref={containerRef}>
			{isLoading ? <div className={styles.centerAlign}><LoadingSpinner /></div> :
				width > 0 && height > 0 && (
					<>
						<div className={styles.donutWrapper} style={{ width: donutAreaWidth, height: donutAreaHeight }}>
							<svg width={donutAreaWidth} height={donutAreaHeight}>
								<VictoryPie
									standalone={false}
									colorScale={colorScale}
									innerRadius={pieInnerRadius}
									radius={pieRadius}
									width={donutAreaWidth}
									height={donutAreaHeight}
									data={itemsData}
									x={d => getTypeName(d as TopMaintenanceTagDashboardDto)}
									y="totalCost"
									events={pieEvents}
									style={{ data: { cursor: 'pointer', stroke: '#fff', strokeWidth: 1 } }}
									labelComponent={<VictoryLabel text="" />}
								/>
							</svg>
							{(() => {
								// Centro do donut em HTML (igual aos outros donuts do dashboard):
								// TOTAL em cima uppercase, valor em baixo bold, € colado ao número,
								// auto-shrink para caber em 1 linha em valores grandes.
								const totalText = Utils.formatCurrency(total || 0).replace(' €', '€');
								const innerWidth = pieInnerRadius * 1.7;
								const baseFontSize = pieInnerRadius * 0.28;
								const approxCharWidth = baseFontSize * 0.55;
								const textWidth = totalText.length * approxCharWidth;
								const fitFactor = textWidth > innerWidth ? innerWidth / textWidth : 1;
								const fontSize = Math.max(baseFontSize * fitFactor, 9);
								return (
									<div
										className={`${styles.centerLabel} ${styles.centerLabelClickable}`}
										style={{ maxWidth: innerWidth }}
										onClick={() => openMaintenancesList({ dateFrom, dateTo })}
									>
										<div className={styles.centerLabelSub}>{t('dashboard.maintenance_total_label' as any)}</div>
										<div className={styles.centerLabelValue} style={{ fontSize }}>{totalText}</div>
									</div>
								);
							})()}
						</div>
						<div className={`${styles.htmlLegend} ${isSideLayout ? styles.htmlLegendSide : ''}`}>
							{sortedItems.map(({ item, color, name }) => {
								const hidden = hiddenData.includes(item.typeId);
								const pct = grandTotal > 0 ? Math.round((item.totalCost / grandTotal) * 100) : 0;
								return (
									<span
										key={item.typeId}
										className={`${styles.legendItem} ${hidden ? styles.legendItemHidden : ''}`}
										onClick={() => onClickLegend(item.typeId)}
									>
										<span className={styles.legendDot} style={{ backgroundColor: hidden ? '#d1d5db' : color }} />
										<span className={styles.legendName}>{name}</span>
										<span className={styles.legendPct}>{pct}%</span>
									</span>
								);
							})}
						</div>
					</>
				)}

			{tooltip && (
				<ChartTooltipPortal clientX={tooltip.clientX} clientY={tooltip.clientY} className={styles.tooltip}>
					<div className={styles.tooltipRow}>
						<span className={styles.tooltipDot} style={{ backgroundColor: tooltip.color }} />
						<span>{tooltip.name}</span>
					</div>
					<div className={styles.tooltipValue}>
						{Utils.formatCurrency(tooltip.value)}
					</div>
				</ChartTooltipPortal>
			)}
		</div>
	);

	if (isLegacyMode) {
		return chartContent;
	}

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.top_maintenance_tags') as string}
			subTitleTooltip={t('dashboard.top_maintenance_tags_tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			{chartContent}
		</Box>
	);
};

export default TopMaintenanceTags;
